import styled from "styled-components";
import { colors } from "../variables";

export const StyledFaq = styled.section`
  background: ${colors.white};
  padding: 4rem 0 6rem;
  display: flex;
  justify-content: center;
`;

export const StyledContainer = styled.div`
  width: 1400px;

  @media (max-width: 1600px) {
    width: 1000px;
  }
  @media (max-width: 1144px) {
    width: 90vw;
  }
  @media (max-width: 768px) {
    width: 95vw;
  }
`;

export const StyledFaqSection = styled.div`
  margin-bottom: 3.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const StyledSectionTitle = styled.h2`
  background: ${colors.dark};
  color: ${colors.white};
  padding: 1.25rem 2rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 0.5rem 0.5rem 0 0;
  margin: 0;

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;
    font-size: 0.85rem;
  }
`;

export const StyledAccordionList = styled.div`
  background: ${colors.white};
  border-radius: 0 0 0.5rem 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  overflow: hidden;
`;

export const StyledAccordionItem = styled.div<{ $isOpen: boolean }>`
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  border-left: 3px solid
    ${(props) => (props.$isOpen ? colors.primary : "transparent")};
  transition: border-left-color 0.3s ease;

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledAccordionHeader = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  background: none;
  border: none;
  padding: 1.25rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  gap: 1rem;
  transition: background-color 0.2s ease;
  background: ${(props) => (props.$isOpen ? "rgba(218, 0, 38, 0.04)" : "")};

  &:hover {
    background: rgba(218, 0, 38, 0.04);
  }

  .question-text {
    font-size: 1rem;
    font-weight: 600;
    color: ${(props) => (props.$isOpen ? colors.primary : colors.dark)};
    line-height: 1.5;
    transition: color 0.3s ease;
  }

  .chevron {
    flex-shrink: 0;
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.primary};
    font-size: 0.75rem;
    transform: ${(props) =>
      props.$isOpen ? "rotate(180deg)" : "rotate(0deg)"};
    transition: transform 0.35s ease;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem;

    .question-text {
      font-size: 0.9rem;
    }
  }
`;

export const StyledAnswerContent = styled.div`
  padding: 1rem 1.75rem;

  p {
    color: ${colors.grayText};
    line-height: 1.8;
    margin-bottom: 0.75rem;
    font-size: 0.95rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ol,
  ul {
    margin: 0.5rem 0 0.75rem 0;
    padding-left: 1.5rem;

    li {
      color: ${colors.grayText};
      line-height: 1.8;
      margin-bottom: 0.5rem;
      font-size: 0.95rem;

      &:last-child {
        margin-bottom: 0;
      }

      ul {
        margin: 0.35rem 0 0 0;
      }
    }
  }

  strong {
    color: ${colors.dark};
    font-weight: 700;
  }

  .warning {
    background: rgba(218, 0, 38, 0.05);
    border-left: 3px solid ${colors.primary};
    border-radius: 0 0.35rem 0.35rem 0;
    padding: 0.85rem 1.1rem;
    margin: 0.75rem 0;

    p {
      margin: 0;
      font-size: 0.9rem;

      & + p {
        margin-top: 0.5rem;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 0.25rem 1.25rem 1.25rem 1.25rem;

    p,
    li {
      font-size: 0.88rem;
    }
  }
`;

export const StyledFormSection = styled.section`
  background: ${colors.lightBg};
  display: flex;
  justify-content: center;
  padding: 4rem 0 6rem;

  @media (max-width: 768px) {
    padding: 3rem 1rem 4rem;
  }
`;

export const StyledFormCard = styled.div`
  width: 100%;
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-top: 4px solid ${colors.dark};
  border-radius: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  overflow: hidden;
`;

export const StyledFormTitle = styled.h2`
  color: ${colors.primary};
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: center;
  margin: 0 0 2rem;

  &:after {
    content: "";
    display: block;
    width: 4rem;
    height: 3px;
    margin: 0.75rem auto 0;
    background: ${colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 1.5rem;
  }
`;

export const StyledFormIntro = styled.div`
  padding: 2rem 2rem 0;
  color: ${colors.dark};

  strong {
    display: block;
    font-size: 1.15rem;
    line-height: 1.5;
  }

  ul {
    list-style: none;
    margin: 1rem 0 0;
    padding: 0;
  }

  li {
    color: ${colors.grayText};
    font-size: 0.95rem;
    line-height: 1.6;

    & + li {
      margin-top: 0.45rem;
    }

    &:before {
      content: "✔";
      color: ${colors.primary};
      font-weight: 700;
      margin-right: 0.55rem;
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem 0;

    strong {
      font-size: 1.05rem;
    }

    li {
      font-size: 0.88rem;
    }
  }
`;

export const StyledForm = styled.form`
  padding: 2rem;

  .field {
    margin-bottom: 1.25rem;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${colors.dark};
  }

  input {
    width: 100%;
    font-size: 1rem;
    line-height: 1.5;
    color: ${colors.grayText};
    background: ${colors.white};
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    padding: 0.6rem;
    outline: none;
    transition: border-color 0.2s ease;

    &:focus {
      border-color: ${colors.primary};
    }
  }

  .has-error {
    border-color: ${colors.primary};
    background: rgba(218, 0, 38, 0.05);
  }

  .error {
    display: block;
    margin-top: 0.35rem;
    color: ${colors.primary};
    font-size: 0.85rem;
  }

  .consent-label {
    font-size: 0.8rem;
    line-height: 1.5;
    color: ${colors.grayText};
  }

  .consent-img {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

// Useful-links block sits on a white band (the FAQ accordions below are on the
// light-grey band) so the page alternates background shades as you scroll.
export const StyledLinks = styled.section`
  background: ${colors.lightBg};
  display: flex;
  justify-content: center;
  padding: 4rem 0 2rem;

  @media (max-width: 768px) {
    padding: 3rem 0 1.5rem;
  }
`;

export const StyledLinksCard = styled.div`
  width: 100%;
  background: ${colors.white};
  border-radius: 0 0 0.5rem 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  overflow: hidden;
`;

export const StyledLinksContent = styled.div`
  padding: 1.5rem 1.75rem 2rem;

  .intro {
    color: ${colors.grayText};
    line-height: 1.8;
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
  }

  .doc-list {
    list-style: none;
    margin: 0 0 1.5rem;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.85rem 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.07);

      &:last-child {
        border-bottom: none;
      }

      button {
        flex-shrink: 0;
        white-space: nowrap;
      }
    }
  }

  .doc-label {
    color: ${colors.grayText};
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .free-note {
    color: ${colors.dark};
    font-weight: 700;
    font-size: 0.95rem;

    &:before {
      content: "✔ ";
      color: ${colors.primary};
    }
  }

  .instagram {
    margin-top: 1.75rem;
    background: ${colors.lightBg};
    border-left: 3px solid ${colors.primary};
    border-radius: 0 0.35rem 0.35rem 0;
    padding: 1.25rem 1.35rem;
  }

  .instagram-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${colors.dark};
    margin-bottom: 0.5rem;

    p {
      font-weight: 700;
      font-size: 1rem;
      line-height: 1.5;
    }
  }

  .instagram-text {
    color: ${colors.grayText};
    line-height: 1.8;
    font-size: 0.95rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1.25rem 1.5rem;

    .intro,
    .doc-label,
    .instagram-text {
      font-size: 0.88rem;
    }

    .doc-list li {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;
